import { registerIndicator, type IndicatorDrawParams, type KLineData } from 'klinecharts'
import { registerOnce } from './registrationUtils'

export interface SessionBreaksConfig {
  color: string
  timezone: string
}

const INDICATOR_NAME = 'SESSION_BREAKS'

// Cache DateTimeFormat per timezone
const dtfCache = new Map<string, Intl.DateTimeFormat>()

function getDateStr(timestamp: number, tz: string): string {
  let dtf = dtfCache.get(tz)
  if (!dtf) {
    dtf = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
    dtfCache.set(tz, dtf)
  }
  return dtf.format(new Date(timestamp))
}

/**
 * Indices in [from, to) whose bar starts a new trading day relative to the
 * previous bar (in the given timezone). Index 0 is never a break — there is
 * no prior bar to break from.
 */
export function findDayBreakIndices(
  dataList: Array<{ timestamp: number } | undefined>,
  from: number,
  to: number,
  tz: string,
): number[] {
  const breaks: number[] = []
  for (let i = Math.max(from, 1); i < to; i++) {
    const bar = dataList[i]
    const prev = dataList[i - 1]
    if (!bar || !prev) continue
    if (getDateStr(bar.timestamp, tz) !== getDateStr(prev.timestamp, tz)) {
      breaks.push(i)
    }
  }
  return breaks
}

/**
 * Vertical dashed lines at the boundary between trading days (the
 * "session breaks"). Drawn on the candle pane for intraday timeframes; the
 * line sits on the left edge of each day's first bar.
 */
export const registerSessionBreaksIndicator = registerOnce(() => {
  registerIndicator({
    name: INDICATOR_NAME,
    shortName: '',
    figures: [],
    calc: (dataList: KLineData[]) => {
      return dataList.map(() => ({}))
    },
    draw: (params: IndicatorDrawParams) => {
      const { ctx, kLineDataList, visibleRange, bounding, barSpace, xAxis, indicator } = params
      const config = indicator.extendData as SessionBreaksConfig | undefined
      if (!config) return false

      const tz = config.timezone || 'America/New_York'
      const { from, to } = visibleRange

      const breaks = findDayBreakIndices(kLineDataList, from, to, tz)
      if (breaks.length === 0) return false

      ctx.save()
      ctx.strokeStyle = config.color
      ctx.lineWidth = 1
      ctx.setLineDash([4, 4])
      for (const i of breaks) {
        const x = xAxis.convertToPixel(i) - barSpace.halfBar
        ctx.beginPath()
        ctx.moveTo(x, 0)
        ctx.lineTo(x, bounding.height)
        ctx.stroke()
      }
      ctx.restore()
      return false // don't prevent other drawing
    },
  })
})

export const SESSION_BREAKS_NAME = INDICATOR_NAME
